import * as React from 'react'

import { Box, Divider, Tag, Text } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import badges from 'modules/billing/lib/assets/badges.png'
import { formatPrice } from 'modules/billing/lib/prices'
import {
  CurrentData,
  usePricingBundleContext,
} from 'modules/onboarding/components/Pricing/PricingBundleContext'
import { usePricingContext } from 'modules/onboarding/components/Pricing/PricingContext'
import { DEFAULT_CURRENCY, MIN_SEATS } from 'modules/onboarding/lib/pricing/plans/utils'

import ItemInfo, { OrderItem } from './_ItemInfo'

const Header = ({ period }: { period: string }) => {
  return (
    <Box flexDirection="row" justifyContent="spaceBetween" flexWrap="wrap" gap="s12">
      <Text.Header2 weight="semibold">
        {i18n.t('pricing_plans.billing_information.summary_card.title')}
      </Text.Header2>
      <Tag>{period}</Tag>
    </Box>
  )
}

const SubtotalInfo = ({ title, price }: { title: string; price: string }) => {
  return (
    <Box flexDirection="row" justifyContent="spaceBetween" gap="s12">
      <Text.Normal color="grey1000">{title}</Text.Normal>
      <Text.Normal color="grey1000" weight="medium">
        {price}
      </Text.Normal>
    </Box>
  )
}

const Badges = () => {
  return (
    <Box alignSelf="center" paddingTop="s16">
      <img src={badges} width="300px" />
    </Box>
  )
}

const SummaryCard = () => {
  const today = new Date()
  const futureDate = new Date(today)
  futureDate.setDate(today.getDate() + 30)

  // Custom bundles data
  const { allPlans, period, computeTotalPrice } = usePricingContext()
  const currency = allPlans.corePlans[0]?.pricing?.currency || DEFAULT_CURRENCY
  const totalPriceCustomBundle = computeTotalPrice(MIN_SEATS)

  // Prebuilt bundles data
  const { currentData: prebuiltBundleData, planType, isYearly } = usePricingBundleContext()
  const totalPricePrebuiltBundle = isYearly
    ? prebuiltBundleData?.yearly.totalPrice || 0
    : prebuiltBundleData?.monthly.totalPrice || 0

  const getTotalPrice = () => {
    return prebuiltBundleData ? totalPricePrebuiltBundle : totalPriceCustomBundle
  }

  const totalPrice = getTotalPrice()

  const getBundleSummary = (currentData: CurrentData) => {
    const titleType =
      planType === 'enterprise'
        ? i18n.t('pricing_plans.config_plan.enterprise')
        : i18n.t('pricing_plans.config_plan.business')
    return [
      {
        name: `${currentData.selectedBundle} ${titleType}`,
        isYearly,
        perSeatPrice: isYearly
          ? currentData.yearly.perSeatPrice / 12
          : currentData.monthly.perSeatPrice,
        feePrice: isYearly ? currentData.yearly.fixedFeePrice : currentData.monthly.fixedFeePrice,
      },
    ]
  }

  const getOrderSummary = (): OrderItem[] => {
    if (prebuiltBundleData) {
      return getBundleSummary(prebuiltBundleData)
    } else {
      return allPlans.corePlans
        .concat(allPlans.normalPlans)
        .filter((plan) => plan.isSelected)
        .map((plan) => {
          return {
            name: plan.title,
            isYearly: period === 'year',
            perSeatPrice: plan.pricing.getSeatsPrice(period) || 0,
            feePrice: plan.pricing.getFeePrice(period),
            tieredPrice: plan.pricing.getTieredPrice(period),
          }
        })
    }
  }

  const getPeriodPayment = (): string => {
    const isYearlyPayment = prebuiltBundleData ? isYearly : period === 'year'
    const periodPayment = isYearlyPayment
      ? i18n.t('pricing_plans.billing_information.summary_card.yearly_payment')
      : i18n.t('pricing_plans.billing_information.summary_card.monthly_payment')

    return periodPayment
  }

  return (
    <>
      <Box
        alignSelf="center"
        background="grey000White"
        padding="s32"
        gap="s16"
        borderRadius={{
          all: 'abs008',
        }}
        style={{ maxWidth: '480px', minWidth: '380px', maxHeight: '70vh', overflowY: 'auto' }}
      >
        <Header period={getPeriodPayment()} />
        <Divider />
        <Box
          paddingY="s8"
          paddingRight="s8"
          gap="s16"
          minHeight="s80"
          maxHeight="s300"
          style={{ overflowY: 'auto' }}
        >
          {getOrderSummary().map((orderItem) => (
            <ItemInfo key={orderItem.name} orderItem={orderItem} currency={currency} />
          ))}
        </Box>
        <Divider />
        <Box paddingTop="s8" gap="s8">
          <SubtotalInfo
            title={i18n.t('pricing_plans.billing_information.summary_card.estimated_total')}
            price={formatPrice(totalPrice, currency)}
          />
        </Box>
        <Box flexDirection="row" justifyContent="spaceBetween">
          <Text.Header2 weight="semibold">
            {i18n.t('pricing_plans.billing_information.summary_card.total')}
          </Text.Header2>
          <Text.Header2 weight="semibold">{formatPrice(totalPrice, currency)}</Text.Header2>
        </Box>
      </Box>
      <Badges />
    </>
  )
}

export default SummaryCard
